import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useAppContext } from '../Store/Store';
import { useLocation } from 'react-router-dom';
import './Management.css';

interface AccessLog {
  id: string;
  product_name: string;
  product_id?: number;
  serial_number: string;
  verification_location: string;
  verification_datetime: string;
  status: string;
  distance: string;
  device?: string;
  os?: string;
  browser?: string;
  ip_address?: string;
  event_name?: string;
  event_id?: number;
}

interface Event {
  id: number;
  name: string;
  code: string;
}

interface Product {
  id: number;
  name: string;
  code: string;
}

interface PaginationInfo {
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

const AccessLogs: React.FC = () => {
  const { backendBaseUrl, user, isAuthenticated } = useAppContext();
  const location = useLocation();

  const [accessLogs, setAccessLogs] = useState<AccessLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [events, setEvents] = useState<Event[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<number | ''>('');
  const [selectedProduct, setSelectedProduct] = useState<number | ''>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(
    null
  );

  useEffect(() => {
    if (isAuthenticated && user?.is_employee) {
      fetchAccessLogs();
      fetchEmployeeEvents();
      if (location.state) {
        const { defaultEvent, defaultProduct } = location.state as {
          defaultEvent?: number;
          defaultProduct?: number;
        };
        if (defaultEvent) setSelectedEvent(defaultEvent);
        if (defaultProduct) setSelectedProduct(defaultProduct);
      }
    }
  }, [isAuthenticated, user, location.state]);

  useEffect(() => {
    if (selectedEvent) fetchEventProducts(selectedEvent);
    else {
      setProducts([]);
      setSelectedProduct('');
    }
  }, [selectedEvent]);

  useEffect(() => {
    fetchAccessLogs();
  }, [
    selectedEvent,
    selectedProduct,
    startDate,
    endDate,
    selectedStatus,
    searchQuery,
    currentPage,
    pageSize,
  ]);

  const fetchEmployeeEvents = async () => {
    try {
      const res = await axios.get(`${backendBaseUrl}/employee/events/`, {
        params: { user_id: user?.id },
      });
      setEvents(res.data);
    } catch {
      console.error('Failed to fetch events');
    }
  };

  const fetchEventProducts = async (eventId: number) => {
    try {
      const res = await axios.get(
        `${backendBaseUrl}/employee/events/${eventId}/products/`,
        { params: { user_id: user?.id } }
      );
      setProducts(res.data.products);
    } catch {
      console.error('Failed to fetch products');
    }
  };

  const fetchAccessLogs = async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = { page: currentPage, page_size: pageSize };
      if (user?.id) params.user_id = user.id;
      if (selectedEvent) params.event_id = selectedEvent;
      if (selectedProduct) params.product_id = selectedProduct;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (selectedStatus)
        params.result = selectedStatus === 'Completed' ? 'success' : 'failure';
      if (searchQuery) params.q = searchQuery;
      const res = await axios.get(`${backendBaseUrl}/employee/access-logs/`, {
        params,
      });
      setAccessLogs(res.data.access_logs);
      setPaginationInfo(res.data.pagination);
    } catch {
      setError('Failed to fetch access logs');
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setSelectedEvent('');
    setSelectedProduct('');
    setStartDate('');
    setEndDate('');
    setSelectedStatus('');
    setCurrentPage(1);
  };

  const statusBadge = (status: string) =>
    status === 'Completed' ? (
      <span className="mgmt-badge mgmt-badge--success">✓ Completed</span>
    ) : (
      <span className="mgmt-badge mgmt-badge--danger">⚠ Attention</span>
    );

  return (
    <>
      <h2 className="mgmt-page-title">Access History</h2>

      {/* Filters */}
      <div className="mgmt-filters">
        <div className="mgmt-filters__search">
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="mgmt-filters__search-input"
            placeholder="Search by product or serial…"
            onKeyDown={e => e.key === 'Enter' && fetchAccessLogs()}
          />
          <button
            className="mgmt-filters__search-btn"
            onClick={fetchAccessLogs}
          >
            🔍
          </button>
        </div>

        <div className="mgmt-filter-group">
          <label>Event</label>
          <select
            value={selectedEvent}
            onChange={e =>
              setSelectedEvent(e.target.value ? Number(e.target.value) : '')
            }
            className="mgmt-select"
          >
            <option value="">All Events</option>
            {events.map(ev => (
              <option key={ev.id} value={ev.id}>
                {ev.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mgmt-filter-group">
          <label>Product</label>
          <select
            value={selectedProduct}
            onChange={e =>
              setSelectedProduct(e.target.value ? Number(e.target.value) : '')
            }
            className="mgmt-select"
            disabled={!selectedEvent}
          >
            <option value="">All Products</option>
            {products.map(p => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mgmt-filter-group">
          <label>Date Range</label>
          <div className="mgmt-date-range">
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="mgmt-date-input"
            />
            <span className="mgmt-date-sep">→</span>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="mgmt-date-input"
            />
          </div>
        </div>

        <div className="mgmt-filter-group">
          <label>Status</label>
          <select
            value={selectedStatus}
            onChange={e => setSelectedStatus(e.target.value)}
            className="mgmt-select"
          >
            <option value="">All Statuses</option>
            <option value="Completed">Completed</option>
            <option value="Attention">Attention</option>
          </select>
        </div>

        <div className="mgmt-filters__footer">
          <div className="mgmt-filters__footer-left">
            <div className="mgmt-page-size">
              <label>Per page</label>
              <select
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {[10, 25, 50].map(n => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button
            onClick={resetFilters}
            className="mgmt-btn mgmt-btn--reset mgmt-btn--sm"
          >
            🧹 Reset filters
          </button>
        </div>
      </div>

      {loading && <div className="mgmt-loading">Loading…</div>}
      {error && <div className="mgmt-error">{error}</div>}

      {!loading && !error && (
        <>
          <div className="mgmt-table-wrap">
            <table className="mgmt-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Serial Number</th>
                  <th>Verification Location</th>
                  <th>Date &amp; Time</th>
                  <th>Status</th>
                  <th>Distance</th>
                </tr>
              </thead>
              <tbody>
                {accessLogs.map(log => (
                  <tr key={log.id}>
                    <td>{log.product_name}</td>
                    <td>
                      <span className="cell-mono">{log.serial_number}</span>
                    </td>
                    <td
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '0.78rem',
                        color: 'var(--mgmt-gray-600)',
                      }}
                    >
                      {log.verification_location}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {log.verification_datetime}
                    </td>
                    <td>{statusBadge(log.status)}</td>
                    <td>{log.distance}</td>
                  </tr>
                ))}
                {accessLogs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="mgmt-table__empty">
                      No access logs found for the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {paginationInfo && (
            <div className="mgmt-pagination">
              <div className="mgmt-pagination__info">
                <span className="mgmt-pagination__count">
                  {(paginationInfo.page - 1) * paginationInfo.page_size + 1}–
                  {Math.min(
                    paginationInfo.page * paginationInfo.page_size,
                    paginationInfo.total_count
                  )}{' '}
                  of {paginationInfo.total_count} entries
                </span>
              </div>
              <div className="mgmt-pagination__pages">
                <button
                  className="mgmt-pagination__btn"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </button>
                <button
                  className="mgmt-pagination__btn"
                  onClick={() => setCurrentPage(p => p - 1)}
                  disabled={currentPage === 1}
                >
                  <FaChevronLeft size={11} /> Prev
                </button>
                <span className="mgmt-pagination__page-label">
                  {currentPage} / {paginationInfo.total_pages}
                </span>
                <button
                  className="mgmt-pagination__btn"
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={currentPage === paginationInfo.total_pages}
                >
                  Next <FaChevronRight size={11} />
                </button>
                <button
                  className="mgmt-pagination__btn"
                  onClick={() => setCurrentPage(paginationInfo.total_pages)}
                  disabled={currentPage === paginationInfo.total_pages}
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default AccessLogs;
